import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import GalleryService from '@/services/gallery.service';
import ForwardArrowIcon from '@/shared-components/icons/components/public/ForwardArrowIcon';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';

import DeleteDraftButton from '../drafts/components/DeleteDraftButton';
import PopUpConfirmDeleteDraft from '../drafts/components/PopUpConfirmDeleteDraft';

import './index.css';

export default function ProfileArtworkDrafts() {
  const { stateHandler } = useStateHandler();
  const { profileData, setProfileData } = useContext(ProfileDataContext);
  const { loading, fetchData, setFetchData, loadMoreData } = useCache(
    `${profileData._id}_artwork_drafts`,
    `${config.apis.api.url}/drafts`,
    {
      injectToken: true,
      invalidateWhen: ["ARTWORK_DRAFT_SAVED", "NEW_ARTWORK_REQUEST", `${profileData._id}_REFRESH`],
    }
  );
  const navigate = useNavigate();
  const [activeDraftId, setActiveDraftId] = useState(null);

  function removeDraft(draftId) {
    const newTotalDocs = fetchData.totalDocs - 1;
    setFetchData({
      ...fetchData,
      totalDocs: newTotalDocs,
      data: fetchData.data.filter((draft) => draft._id !== draftId),
    });
    setProfileData({
      ...profileData,
      draft_count: newTotalDocs,
    });
    GalleryService.removeArtworkDraft(draftId);
    setActiveDraftId(null);
  }

  return (
    <>
      {loading ? (
        <Loader className="artwork-drafts__loading" thin />
      ) : fetchData.data.length === 0 ? (
        <Text className="artwork-drafts__no-drafts" paragraph small>
          No artwork drafts available.
        </Text>
      ) : (
        <div className="artwork-drafts__container">
          <InfiniteScroll
            className="post-tags-virtual-list__container remove-scrollbar"
            onLoadMore={loadMoreData}
            shouldLoadMore={fetchData.data.length < fetchData.totalDocs}
            offsetElements={10}
            listLength={fetchData.data.length}
            defaultInfiniteLoaderClassName="post-tags-virtual-list__spinner"
          >
            {({ infiniteScrollAssigner }) => (
              <WindowScroller>
                {({ height, isScrolling, onChildScroll, scrollTop }) => (
                  <AutoSizer disableHeight>
                    {({ width }) => (
                      <List
                        autoHeight
                        width={width === 0 ? 0.1 : width}
                        height={height * fetchData.data.length - 0.001}
                        isScrolling={isScrolling}
                        onScroll={onChildScroll}
                        rowCount={fetchData.data.length}
                        rowHeight={56}
                        scrollTop={scrollTop}
                        overscanRowCount={10}
                        rowRenderer={({ index, key, style }) => (
                          <div key={key} style={style} ref={infiniteScrollAssigner(index)}>
                            <div
                              className="artwork-drafts__draft element-non-selectable"
                              onClick={() =>
                                navigate("/profile/add-artwork", {
                                  state: {
                                    draftData: fetchData.data[index],
                                    from: true,
                                  },
                                })
                              }
                            >
                              <DeleteDraftButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveDraftId(fetchData.data[index]._id);
                                }}
                              />

                              {activeDraftId === fetchData.data[index]._id && (
                                <PopUpConfirmDeleteDraft
                                  open={activeDraftId === fetchData.data[index]._id}
                                  close={() => setActiveDraftId(null)}
                                  draftName={
                                    fetchData.data[index].draft_container.artwork_about.artwork_title
                                  }
                                  onConfirm={(e) => {
                                    e.stopPropagation();
                                    removeDraft(fetchData.data[index]._id);
                                    stateHandler.set("temporalPopup", {
                                      text: `Draft deleted successfully`,
                                    });
                                  }}
                                />
                              )}

                              <Text className="artwork-drafts__draft-title dots-on-overflow" medium>
                                {fetchData.data[index].draft_container.artwork_about.artwork_title}
                              </Text>
                              <ForwardArrowIcon className="artwork-drafts__enter-draft" />
                            </div>
                          </div>
                        )}
                      />
                    )}
                  </AutoSizer>
                )}
              </WindowScroller>
            )}
          </InfiniteScroll>
        </div>
      )}
    </>
  );
}
